import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Building2, Calendar, DollarSign, Pencil, Trash2,
  StickyNote, Clock, Pin, Users, RotateCcw, XCircle, CheckCircle2,
  FileText, Video, ArrowRightCircle, ExternalLink, FolderPlus, Plus, Mail,
} from 'lucide-react';
import { useDeal, useUpdateDeal, useDeleteDeal, useReopenDeal, useCloseDeal } from '@/hooks/useDeals';
import { useCloseReasons } from '@/hooks/useCloseReasons';
import { useNotes, useCreateNote, useDeleteNote, useToggleNotePin } from '@/hooks/useNotes';
import { useActivities } from '@/hooks/useActivities';
import { useProposals, useCreateProposalFolder, useUpdateProposalStatus, useCreateProposal } from '@/hooks/useProposals';
import { useMeetings } from '@/hooks/useMeetings';
import { useTeamMembers } from '@/hooks/useTeam';
import { conversionApi } from '@/lib/api';
import { Button, Card, ConfirmDialog, Input, Modal, Select, Skeleton, Tabs, Textarea } from '@solid/ui';
import { Badge } from '@/components/ui/Badge';
import { Avatar } from '@/components/ui/Avatar';
import { formatDate, formatRelativeTime, formatCurrency } from '@/lib/utils';
import { CustomFieldsDisplay } from '@/components/CustomFieldsDisplay';
import { CustomFieldsForm } from '@/components/CustomFieldsForm';
import { SendEmailModal } from '@/components/SendEmailModal';
import { PaymentScheduleCard } from '@/components/PaymentScheduleCard';
import type { DealPriority, Deal, ProposalStatus } from '@/types';

const priorityVariant: Record<DealPriority, 'default' | 'info' | 'warning' | 'error'> = {
  LOW: 'default',
  MEDIUM: 'info',
  HIGH: 'warning',
  URGENT: 'error',
};

const proposalStatusVariant: Record<string, 'default' | 'info' | 'warning' | 'success' | 'error'> = {
  DRAFT: 'default',
  SENT: 'info',
  VIEWED: 'warning',
  COMMENTED: 'info',
  REVISION_REQUESTED: 'warning',
  SIGNED: 'success',
  DECLINED: 'error',
};

const proposalStatusOptions: { value: ProposalStatus; label: string }[] = [
  { value: 'DRAFT', label: 'Draft' },
  { value: 'SENT', label: 'Sent' },
  { value: 'VIEWED', label: 'Viewed' },
  { value: 'COMMENTED', label: 'Commented' },
  { value: 'REVISION_REQUESTED', label: 'Revision Requested' },
  { value: 'SIGNED', label: 'Signed' },
  { value: 'DECLINED', label: 'Declined' },
];

export default function DealDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: deal, isLoading } = useDeal(id);
  const [tab, setTab] = useState('overview');
  const [showEdit, setShowEdit] = useState(false);
  const [showClose, setShowClose] = useState(false);
  const [showConvert, setShowConvert] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [sendEmailContact, setSendEmailContact] = useState<{ id: string; firstName: string; lastName: string; email?: string } | null>(null);

  const deleteDeal = useDeleteDeal();
  const reopenDeal = useReopenDeal();

  const handleDelete = async () => {
    if (!id) return;
    await deleteDeal.mutateAsync(id);
    navigate('/admin/deals');
  };

  const handleReopen = async () => {
    if (!id) return;
    await reopenDeal.mutateAsync(id);
  };

  if (isLoading) {
    return (
      <div className="page-enter">
        <div className="space-y-4">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-40 rounded-xl" />
        </div>
      </div>
    );
  }

  if (!deal) return null;

  const isClosed = !!deal.closedAt;
  const isWon = deal.stage.isWon === true;
  const canConvert = isWon && !(deal as any).ecosystemOrgId;

  return (
    <div className="page-enter">
      <div className="mb-6">
        <Link to="/admin/deals" className="inline-flex items-center gap-1 text-sm text-grey-500 hover:text-secondary mb-4">
          <ArrowLeft className="w-4 h-4" />
          Back to Deals
        </Link>

        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-heading-1 text-secondary">{deal.title}</h1>
            <div className="flex items-center gap-3 mt-2 flex-wrap">
              <Badge
                variant={deal.stage.isWon ? 'success' : deal.stage.isTerminal ? 'error' : 'info'}
              >
                {deal.stage.name}
              </Badge>
              <Badge variant={priorityVariant[deal.priority]}>{deal.priority}</Badge>
              {isClosed && deal.closeReason && (
                <Badge variant="error">{deal.closeReason.label}</Badge>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {canConvert && (
              <Button variant="outline" size="sm" onClick={() => setShowConvert(true)}>
                <ArrowRightCircle className="w-3.5 h-3.5" />
                Convert to Client
              </Button>
            )}
            {isClosed ? (
              <Button variant="outline" size="sm" onClick={handleReopen} loading={reopenDeal.isPending}>
                <RotateCcw className="w-3.5 h-3.5" />
                Reopen
              </Button>
            ) : (
              <>
                <Button variant="outline" size="sm" onClick={() => setShowClose(true)}>
                  <XCircle className="w-3.5 h-3.5" />
                  Close
                </Button>
                <Button variant="outline" size="sm" onClick={() => setShowEdit(true)}>
                  <Pencil className="w-3.5 h-3.5" />
                  Edit
                </Button>
              </>
            )}
            <Button variant="destructive" size="sm" aria-label="Delete deal" onClick={() => setShowDelete(true)}>
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      </div>

      {/* Stage progress bar */}
      {deal.stageHistory && deal.stageHistory.length > 0 && (
        <Card className="mb-6" padding="sm">
          <StageProgressBar deal={deal} />
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Sidebar */}
        <div className="space-y-4">
          <Card padding="md">
            <h3 className="section-header mb-4">Deal Info</h3>
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm">
                <DollarSign className="w-4 h-4 text-grey-400" />
                <span className="text-grey-600 font-semibold">
                  {deal.value ? formatCurrency(Number(deal.value)) : 'No value set'}
                </span>
              </div>
              {deal.dueDate && (
                <div className="flex items-center gap-2 text-sm">
                  <Calendar className="w-4 h-4 text-grey-400" />
                  <span className="text-grey-600">Due: {formatDate(deal.dueDate)}</span>
                </div>
              )}
              {deal.organization && (
                <div className="flex items-center gap-2 text-sm">
                  <Building2 className="w-4 h-4 text-grey-400" />
                  <Link to={`/admin/organizations/${deal.organization.id}`} className="link">
                    {deal.organization.name}
                  </Link>
                </div>
              )}
              <DealOwnerSelector deal={deal} />
            </div>
          </Card>

          {deal.dealContacts.length > 0 && (
            <Card padding="md">
              <h3 className="section-header mb-4">Contacts</h3>
              <div className="space-y-2">
                {deal.dealContacts.map((dc) => (
                  <div
                    key={dc.id}
                    className="flex items-center justify-between p-2 rounded-lg hover:bg-grey-50 transition-colors"
                  >
                    <Link
                      to={`/admin/contacts/${dc.contact.id}`}
                      className="flex-1 text-sm font-medium text-secondary hover:text-primary"
                    >
                      {dc.contact.firstName} {dc.contact.lastName}
                    </Link>
                    <div className="flex items-center gap-2">
                      {dc.contact.email && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setSendEmailContact(dc.contact)}
                          aria-label="Send email"
                        >
                          <Mail className="w-4 h-4" />
                        </Button>
                      )}
                      <Badge variant="outline" size="sm">{dc.role}</Badge>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {deal.description && (
            <Card padding="md">
              <h3 className="section-header mb-2">Description</h3>
              <p className="text-sm text-grey-600 whitespace-pre-wrap">{deal.description}</p>
            </Card>
          )}

          <Card padding="md">
            <h3 className="section-header mb-3">Custom Fields</h3>
            <CustomFieldsDisplay entityType="DEAL" customFields={deal.customFields} />
          </Card>

          <Card padding="md">
            <h3 className="section-header mb-2">Created</h3>
            <p className="text-body-sm text-grey-500">{formatDate(deal.createdAt)}</p>
            {deal.closedAt && (
              <>
                <h3 className="section-header mb-2 mt-4">Closed</h3>
                <p className="text-body-sm text-grey-500">{formatDate(deal.closedAt)}</p>
              </>
            )}
          </Card>
        </div>

        {/* Main content */}
        <div className="lg:col-span-2">
          <PaymentScheduleCard deal={deal} />

          <Tabs
            value={tab}
            onValueChange={setTab}
            items={[
              { value: 'overview', label: 'Activity', content: <DealActivityTimeline dealId={id!} /> },
              { value: 'notes', label: 'Notes', content: <DealNotesSection dealId={id!} /> },
              { value: 'history', label: 'History', content: <StageHistoryTable deal={deal} /> },
              { value: 'proposals', label: 'Proposals', content: <DealProposalsSection dealId={id!} /> },
              { value: 'meetings', label: 'Meetings', content: <DealMeetingsSection dealId={id!} /> },
              ...(deal.metadata
                ? [{ value: 'submission', label: 'Submission', content: <SubmissionDetailsSection metadata={deal.metadata} /> }]
                : []),
            ]}
          />
        </div>
      </div>

      <ConfirmDialog
        open={showDelete}
        onOpenChange={setShowDelete}
        title="Delete deal"
        description="Are you sure you want to delete this deal? This cannot be undone."
        confirmLabel="Delete"
        destructive
        onConfirm={handleDelete}
      />

      {showEdit && (
        <EditDealDialog deal={deal} open={showEdit} onOpenChange={setShowEdit} />
      )}

      {showClose && (
        <CloseDealFromDetail dealId={deal.id} open={showClose} onOpenChange={setShowClose} />
      )}

      {showConvert && (
        <ConvertDealDialog deal={deal} open={showConvert} onOpenChange={setShowConvert} />
      )}

      {sendEmailContact && (
        <SendEmailModal
          open={!!sendEmailContact}
          onOpenChange={(open) => !open && setSendEmailContact(null)}
          contact={sendEmailContact}
          dealId={deal.id}
          dealName={deal.title}
        />
      )}
    </div>
  );
}

function StageProgressBar({ deal }: { deal: Deal }) {
  const history = deal.stageHistory ?? [];

  return (
    <div className="flex items-center gap-1 overflow-x-auto py-2 px-1">
      {history.map((entry, i) => (
        <div key={entry.id} className="flex items-center gap-1">
          {i > 0 && <div className="w-4 h-0.5 bg-grey-300" />}
          <div className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-grey-50 flex-shrink-0">
            <div
              className="w-2.5 h-2.5 rounded-full"
              style={{ backgroundColor: entry.stage.color }}
            />
            <span className="text-xs font-medium text-grey-600 whitespace-nowrap">
              {entry.stage.name}
            </span>
            {entry.exitedAt ? (
              <CheckCircle2 className="w-3 h-3 text-success" />
            ) : (
              <div className="w-3 h-3 rounded-full border-2 border-primary animate-pulse" />
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function StageHistoryTable({ deal }: { deal: Deal }) {
  const history = deal.stageHistory ?? [];

  if (!history.length) {
    return (
      <Card padding="md">
        <div className="text-center py-8">
          <Clock className="w-8 h-8 text-grey-300 mx-auto mb-2" />
          <p className="text-body-sm text-grey-500">No stage history.</p>
        </div>
      </Card>
    );
  }

  return (
    <Card padding="none">
      <table className="w-full">
        <thead>
          <tr className="border-b border-grey-200">
            <th className="text-left text-xs font-semibold text-grey-500 uppercase tracking-wider px-4 py-3">Stage</th>
            <th className="text-left text-xs font-semibold text-grey-500 uppercase tracking-wider px-4 py-3">Entered</th>
            <th className="text-left text-xs font-semibold text-grey-500 uppercase tracking-wider px-4 py-3">Exited</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-grey-100">
          {history.map((entry) => (
            <tr key={entry.id}>
              <td className="px-4 py-3">
                <div className="flex items-center gap-2">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: entry.stage.color }}
                  />
                  <span className="text-sm font-medium text-secondary">{entry.stage.name}</span>
                </div>
              </td>
              <td className="px-4 py-3 text-sm text-grey-500">{formatDate(entry.enteredAt)}</td>
              <td className="px-4 py-3 text-sm text-grey-500">
                {entry.exitedAt ? formatDate(entry.exitedAt) : (
                  <Badge variant="info" size="sm">Current</Badge>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

function DealActivityTimeline({ dealId }: { dealId: string }) {
  const { data: activities, isLoading } = useActivities({ dealId });

  if (isLoading) return <Skeleton className="h-32 rounded-xl" />;

  if (!activities?.length) {
    return (
      <Card padding="md">
        <div className="text-center py-8">
          <Clock className="w-8 h-8 text-grey-300 mx-auto mb-2" />
          <p className="text-body-sm text-grey-500">No activity yet.</p>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      {activities.map((activity) => (
        <Card key={activity.id} padding="sm">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-medium text-secondary">
                {activity.type.replace(/_/g, ' ')}
              </p>
              {activity.actor && <p className="text-xs text-grey-500">by {activity.actor.name}</p>}
              {activity.metadata && (
                <div className="mt-1 text-xs text-grey-500">
                  {activity.metadata.fromStage && activity.metadata.toStage && (
                    <span>{activity.metadata.fromStage} → {activity.metadata.toStage}</span>
                  )}
                  {activity.metadata.oldValue !== undefined && activity.metadata.newValue !== undefined && (
                    <span>
                      {formatCurrency(Number(activity.metadata.oldValue))} → {formatCurrency(Number(activity.metadata.newValue))}
                    </span>
                  )}
                </div>
              )}
            </div>
            <span className="text-xs text-grey-400">{formatRelativeTime(activity.createdAt)}</span>
          </div>
        </Card>
      ))}
    </div>
  );
}

function DealNotesSection({ dealId }: { dealId: string }) {
  const { data: notes, isLoading } = useNotes({ dealId });
  const createNote = useCreateNote();
  const deleteNote = useDeleteNote();
  const togglePin = useToggleNotePin();
  const [newNote, setNewNote] = useState('');

  const handleCreate = async () => {
    if (!newNote.trim()) return;
    await createNote.mutateAsync({ content: newNote, dealId });
    setNewNote('');
  };

  return (
    <div className="space-y-4">
      <Card padding="md">
        <Textarea
          placeholder="Add a note..."
          value={newNote}
          onChange={(e) => setNewNote(e.target.value)}
          className="mb-3"
        />
        <div className="flex justify-end">
          <Button size="sm" onClick={handleCreate} loading={createNote.isPending} disabled={!newNote.trim()}>
            <StickyNote className="w-3.5 h-3.5" />
            Add Note
          </Button>
        </div>
      </Card>

      {isLoading ? (
        <Skeleton className="h-32 rounded-xl" />
      ) : !notes?.length ? (
        <Card padding="md">
          <div className="text-center py-8">
            <StickyNote className="w-8 h-8 text-grey-300 mx-auto mb-2" />
            <p className="text-body-sm text-grey-500">No notes yet.</p>
          </div>
        </Card>
      ) : (
        notes.map((note) => (
          <Card key={note.id} padding="sm">
            <div className="flex items-start justify-between mb-2">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-secondary">{note.author.name}</span>
                <span className="text-xs text-grey-400">{formatRelativeTime(note.createdAt)}</span>
                {note.isPinned && <Pin className="w-3 h-3 text-primary" />}
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => togglePin.mutate(note.id)}
                  className="p-1 rounded hover:bg-grey-100 text-grey-400 hover:text-grey-600"
                >
                  <Pin className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => deleteNote.mutate(note.id)}
                  className="p-1 rounded hover:bg-grey-100 text-grey-400 hover:text-error"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
            <div className="text-sm text-grey-600 whitespace-pre-wrap">{note.content}</div>
          </Card>
        ))
      )}
    </div>
  );
}

function DealProposalsSection({ dealId }: { dealId: string }) {
  const { data: proposals, isLoading } = useProposals(dealId);
  const createFolder = useCreateProposalFolder();
  const updateStatus = useUpdateProposalStatus();
  const createProposal = useCreateProposal();
  const [showLinkDoc, setShowLinkDoc] = useState(false);
  const [linkForm, setLinkForm] = useState({ driveFileId: '', name: '', webViewLink: '' });

  const handleLinkDocument = async () => {
    if (!linkForm.driveFileId.trim() || !linkForm.name.trim()) return;
    await createProposal.mutateAsync({
      dealId,
      driveFileId: linkForm.driveFileId,
      name: linkForm.name,
      webViewLink: linkForm.webViewLink || undefined,
    });
    setLinkForm({ driveFileId: '', name: '', webViewLink: '' });
    setShowLinkDoc(false);
  };

  const handleStatusChange = (proposalId: string, newStatus: string) => {
    updateStatus.mutate({ id: proposalId, status: newStatus });
  };

  if (isLoading) return <Skeleton className="h-32 rounded-xl" />;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => createFolder.mutate(dealId)}
          loading={createFolder.isPending}
        >
          <FolderPlus className="w-3.5 h-3.5" />
          Create Folder
        </Button>
        <Button variant="outline" size="sm" onClick={() => setShowLinkDoc(true)}>
          <Plus className="w-3.5 h-3.5" />
          Link Document
        </Button>
      </div>

      {!proposals?.length ? (
        <Card padding="md">
          <div className="text-center py-8">
            <FileText className="w-8 h-8 text-grey-300 mx-auto mb-2" />
            <p className="text-body-sm text-grey-500">No proposals yet.</p>
          </div>
        </Card>
      ) : (
        proposals.map((proposal) => (
          <Card key={proposal.id} padding="sm">
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <FileText className="w-4 h-4 text-grey-400 flex-shrink-0" />
                  <span className="text-sm font-medium text-secondary truncate">
                    {proposal.name}
                  </span>
                  <Badge variant={proposalStatusVariant[proposal.status] || 'default'} size="sm">
                    {proposal.status}
                  </Badge>
                </div>
                <div className="flex items-center gap-4 mt-2 text-xs text-grey-500 flex-wrap">
                  <span>Created: {formatDate(proposal.createdAt)}</span>
                  {proposal.sentAt && <span>Sent: {formatDate(proposal.sentAt)}</span>}
                  {proposal.viewedAt && <span>Viewed: {formatDate(proposal.viewedAt)}</span>}
                  {proposal.signedAt && <span>Signed: {formatDate(proposal.signedAt)}</span>}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0 ml-4">
                <Select
                  value={proposal.status}
                  onChange={(e) => handleStatusChange(proposal.id, e.target.value)}
                  options={proposalStatusOptions}
                />
                {proposal.webViewLink && (
                  <a
                    href={proposal.webViewLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-1.5 rounded hover:bg-grey-100 text-grey-400 hover:text-secondary transition-colors"
                    title="Open in Google Drive"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </a>
                )}
              </div>
            </div>
          </Card>
        ))
      )}

      {showLinkDoc && (
        <Modal
          open={showLinkDoc}
          onOpenChange={setShowLinkDoc}
          title="Link Document"
          footer={
            <>
              <Button variant="ghost" onClick={() => setShowLinkDoc(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleLinkDocument}
                loading={createProposal.isPending}
                disabled={!linkForm.driveFileId.trim() || !linkForm.name.trim()}
              >
                Link Document
              </Button>
            </>
          }
        >
            <div className="space-y-4">
              <Input
                label="Document Name"
                required
                value={linkForm.name}
                onChange={(e) => setLinkForm({ ...linkForm, name: e.target.value })}
                placeholder="e.g. Project Proposal v1"
              />
              <Input
                label="Google Drive File ID"
                required
                value={linkForm.driveFileId}
                onChange={(e) => setLinkForm({ ...linkForm, driveFileId: e.target.value })}
                placeholder="e.g. 1BxiMVs0XRA5nFMdKvBdBZjgmUUqptlbs74OgVE2upms"
              />
              <Input
                label="Web View Link (optional)"
                value={linkForm.webViewLink}
                onChange={(e) => setLinkForm({ ...linkForm, webViewLink: e.target.value })}
                placeholder="https://docs.google.com/..."
              />
            </div>
        </Modal>
      )}
    </div>
  );
}

function DealMeetingsSection({ dealId }: { dealId: string }) {
  const { data: meetings, isLoading } = useMeetings({ dealId });
  const [expandedId, setExpandedId] = useState<string | null>(null);

  if (isLoading) return <Skeleton className="h-32 rounded-xl" />;

  if (!meetings?.length) {
    return (
      <Card padding="md">
        <div className="text-center py-8">
          <Video className="w-8 h-8 text-grey-300 mx-auto mb-2" />
          <p className="text-body-sm text-grey-500">No meetings yet.</p>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      {meetings.map((meeting) => {
        const isExpanded = expandedId === meeting.id;
        const attendeeCount = meeting.attendees?.length ?? 0;
        const summaryPreview = meeting.summary
          ? meeting.summary.length > 200
            ? meeting.summary.slice(0, 200) + '...'
            : meeting.summary
          : null;

        return (
          <Card key={meeting.id} padding="sm">
            <div
              className="cursor-pointer"
              onClick={() => setExpandedId(isExpanded ? null : meeting.id)}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <Video className="w-4 h-4 text-grey-400 flex-shrink-0" />
                    <span className="text-sm font-medium text-secondary truncate">
                      {meeting.title}
                    </span>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-grey-500 flex-wrap">
                    <span>{formatDate(meeting.meetingDate)}</span>
                    {meeting.duration != null && (
                      <span>{meeting.duration} min</span>
                    )}
                    {attendeeCount > 0 && (
                      <span className="flex items-center gap-1">
                        <Users className="w-3 h-3" />
                        {attendeeCount} attendee{attendeeCount !== 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {!isExpanded && summaryPreview && (
                <p className="mt-2 text-xs text-grey-500 line-clamp-2">{summaryPreview}</p>
              )}
            </div>

            {isExpanded && (
              <div className="mt-4 space-y-4 border-t border-grey-100 pt-4">
                {meeting.summary && (
                  <div>
                    <h4 className="text-xs font-semibold text-grey-500 uppercase tracking-wider mb-2">
                      Summary
                    </h4>
                    <p className="text-sm text-grey-600 whitespace-pre-wrap">{meeting.summary}</p>
                  </div>
                )}

                {meeting.attendees && meeting.attendees.length > 0 && (
                  <div>
                    <h4 className="text-xs font-semibold text-grey-500 uppercase tracking-wider mb-2">
                      Attendees
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {meeting.attendees.map((attendee, i) => (
                        <Badge key={i} variant="outline" size="sm">
                          {attendee.name} ({attendee.email})
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {meeting.actionItems && meeting.actionItems.length > 0 && (
                  <div>
                    <h4 className="text-xs font-semibold text-grey-500 uppercase tracking-wider mb-2">
                      Action Items
                    </h4>
                    <ul className="space-y-1.5">
                      {meeting.actionItems.map((item, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm">
                          <input
                            type="checkbox"
                            checked={item.completed}
                            readOnly
                            className="mt-0.5 rounded border-grey-300"
                          />
                          <span className={item.completed ? 'text-grey-400 line-through' : 'text-grey-600'}>
                            {item.text}
                            {item.assignee && (
                              <span className="text-xs text-grey-400 ml-1">({item.assignee})</span>
                            )}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {meeting.recordingUrl && (
                  <a
                    href={meeting.recordingUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                    View Recording
                  </a>
                )}
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
}

interface SubmissionMetadata {
  contact: { firstName: string; lastName: string; email: string; company: string };
  projectDescription: string;
  dealType: string | null;
  qualificationScore: number;
  qualifyingAnswers: Array<{ question: string; answer: string; source: 'document' | 'wizard' }>;
  documentSummary?: string;
  aiSummary: string;
  submissionUrl?: string;
  submissionId?: string;
  budget?: string;
  timeline?: string;
  techRequirements?: string;
  documents?: Array<{ fileName: string; storageKey: string }>;
}

function SubmissionDetailsSection({ metadata }: { metadata: unknown }) {
  const data = metadata as SubmissionMetadata;

  const scoreColor =
    data.qualificationScore >= 80
      ? 'text-success'
      : data.qualificationScore >= 60
        ? 'text-warning'
        : 'text-error';

  const scoreBarColor =
    data.qualificationScore >= 80
      ? 'bg-success'
      : data.qualificationScore >= 60
        ? 'bg-warning'
        : 'bg-error';

  const scoreBadgeVariant: 'success' | 'warning' | 'error' =
    data.qualificationScore >= 80
      ? 'success'
      : data.qualificationScore >= 60
        ? 'warning'
        : 'error';

  return (
    <div className="space-y-4">
      {/* AI Qualification Score */}
      <Card padding="md">
        <h3 className="section-header mb-4">AI Qualification Score</h3>
        <div className="flex items-center gap-3 mb-3">
          <Badge variant={scoreBadgeVariant} className="text-lg px-3 py-1">
            {data.qualificationScore}
          </Badge>
          <span className={`text-sm font-semibold ${scoreColor}`}>
            {data.qualificationScore >= 80
              ? 'Strong Match'
              : data.qualificationScore >= 60
                ? 'Moderate Match'
                : 'Weak Match'}
          </span>
        </div>
        <div className="w-full bg-grey-200 rounded-full h-2">
          <div
            className={`h-2 rounded-full transition-all ${scoreBarColor}`}
            style={{ width: `${Math.min(100, Math.max(0, data.qualificationScore))}%` }}
          />
        </div>
      </Card>

      {/* AI Summary */}
      {data.aiSummary && (
        <Card padding="md">
          <h3 className="section-header mb-3">AI Summary</h3>
          <p className="text-sm text-grey-600 whitespace-pre-wrap">{data.aiSummary}</p>
        </Card>
      )}

      {/* Document Summary */}
      {data.documentSummary && (
        <Card padding="md">
          <h3 className="section-header mb-3">Document Summary</h3>
          <p className="text-sm text-grey-600 whitespace-pre-wrap">{data.documentSummary}</p>
        </Card>
      )}

      {/* Project Details */}
      <Card padding="md">
        <h3 className="section-header mb-4">Project Details</h3>
        <div className="space-y-3">
          {data.contact && (
            <>
              <div className="flex items-start gap-2 text-sm">
                <span className="text-grey-500 w-28 flex-shrink-0">Name</span>
                <span className="text-grey-700 font-medium">
                  {data.contact.firstName} {data.contact.lastName}
                </span>
              </div>
              <div className="flex items-start gap-2 text-sm">
                <span className="text-grey-500 w-28 flex-shrink-0">Email</span>
                <span className="text-grey-700 font-medium">{data.contact.email}</span>
              </div>
              <div className="flex items-start gap-2 text-sm">
                <span className="text-grey-500 w-28 flex-shrink-0">Company</span>
                <span className="text-grey-700 font-medium">{data.contact.company}</span>
              </div>
            </>
          )}
          {data.dealType && (
            <div className="flex items-start gap-2 text-sm">
              <span className="text-grey-500 w-28 flex-shrink-0">Deal Type</span>
              <span className="text-grey-700 font-medium">{data.dealType}</span>
            </div>
          )}
          {data.projectDescription && (
            <div className="flex items-start gap-2 text-sm">
              <span className="text-grey-500 w-28 flex-shrink-0">Description</span>
              <span className="text-grey-700 whitespace-pre-wrap">{data.projectDescription}</span>
            </div>
          )}
          {data.budget && (
            <div className="flex items-start gap-2 text-sm">
              <span className="text-grey-500 w-28 flex-shrink-0">Budget</span>
              <span className="text-grey-700">{data.budget}</span>
            </div>
          )}
          {data.timeline && (
            <div className="flex items-start gap-2 text-sm">
              <span className="text-grey-500 w-28 flex-shrink-0">Timeline</span>
              <span className="text-grey-700">{data.timeline}</span>
            </div>
          )}
          {data.techRequirements && (
            <div className="flex items-start gap-2 text-sm">
              <span className="text-grey-500 w-28 flex-shrink-0">Tech Reqs</span>
              <span className="text-grey-700">{data.techRequirements}</span>
            </div>
          )}
          {data.documents && data.documents.length > 0 && (
            <div className="flex items-start gap-2 text-sm">
              <span className="text-grey-500 w-28 flex-shrink-0">Documents</span>
              <span className="text-grey-700">{data.documents.map((d) => d.fileName).join(', ')}</span>
            </div>
          )}
          {data.submissionUrl && (
            <div className="flex items-start gap-2 text-sm">
              <span className="text-grey-500 w-28 flex-shrink-0">Submission</span>
              <a
                href={data.submissionUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="link inline-flex items-center gap-1"
              >
                View Original Submission
                <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          )}
        </div>
      </Card>

      {/* Qualifying Answers */}
      {data.qualifyingAnswers && data.qualifyingAnswers.length > 0 && (
        <Card padding="md">
          <h3 className="section-header mb-4">Qualifying Answers</h3>
          <div className="space-y-4">
            {data.qualifyingAnswers.map((qa, i) => (
              <div key={i} className="border-b border-grey-100 pb-3 last:border-0 last:pb-0">
                <div className="flex items-start justify-between gap-2 mb-1">
                  <p className="text-sm font-medium text-secondary">{qa.question}</p>
                  <Badge
                    variant={qa.source === 'document' ? 'info' : 'primary'}
                    size="sm"
                  >
                    {qa.source}
                  </Badge>
                </div>
                <p className="text-sm text-grey-600">{qa.answer}</p>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

function DealOwnerSelector({ deal }: { deal: Deal }) {
  const [isEditing, setIsEditing] = useState(false);
  const { data: teamMembers } = useTeamMembers();
  const updateDeal = useUpdateDeal();

  const handleChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newOwnerId = e.target.value;
    if (newOwnerId && newOwnerId !== deal.ownerId) {
      await updateDeal.mutateAsync({ id: deal.id, data: { ownerId: newOwnerId } });
    }
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <div className="flex items-center gap-2 text-sm">
        <Users className="w-4 h-4 text-grey-400 flex-shrink-0" />
        <Select
          aria-label="Deal owner"
          selectSize="sm"
          className="flex-1"
          value={deal.ownerId}
          onChange={handleChange}
          onBlur={() => setIsEditing(false)}
          autoFocus
          options={[
            ...(teamMembers ?? []).map((m) => ({ value: m.id, label: m.name || m.email })),
            // Ensure current owner is always an option
            ...(teamMembers && !teamMembers.find((m) => m.id === deal.ownerId)
              ? [{ value: deal.ownerId, label: deal.owner.name || 'Current Owner' }]
              : []),
          ]}
        />
      </div>
    );
  }

  return (
    <button
      onClick={() => setIsEditing(true)}
      className="flex items-center gap-2 text-sm w-full text-left hover:bg-grey-50 rounded -mx-1 px-1 py-0.5 transition-colors group"
    >
      <Users className="w-4 h-4 text-grey-400" />
      <span className="text-grey-600">Owner:</span>
      <Avatar src={deal.owner.avatarUrl} name={deal.owner.name} size="xs" />
      <span className="text-grey-600">{deal.owner.name}</span>
      <Pencil className="w-3 h-3 text-grey-300 opacity-0 group-hover:opacity-100 transition-opacity ml-auto" />
    </button>
  );
}

function EditDealDialog({
  deal,
  open,
  onOpenChange,
}: {
  deal: Deal;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [form, setForm] = useState({
    title: deal.title,
    value: deal.value ? String(Number(deal.value)) : '',
    description: deal.description || '',
    priority: deal.priority,
    ownerId: deal.ownerId,
    dueDate: deal.dueDate ? deal.dueDate.split('T')[0] : '',
  });
  const [customFields, setCustomFields] = useState<Record<string, any>>(deal.customFields || {});
  const updateDeal = useUpdateDeal();
  const { data: teamMembers } = useTeamMembers();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await updateDeal.mutateAsync({
      id: deal.id,
      data: {
        title: form.title,
        value: form.value ? Number(form.value) : undefined,
        description: form.description || undefined,
        priority: form.priority,
        ownerId: form.ownerId !== deal.ownerId ? form.ownerId : undefined,
        dueDate: form.dueDate || undefined,
        customFields,
      },
    });
    onOpenChange(false);
  };

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="Edit Deal"
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="submit" form="edit-deal-form" loading={updateDeal.isPending}>
            Save Changes
          </Button>
        </>
      }
    >
      <form id="edit-deal-form" onSubmit={handleSubmit} className="space-y-4">
          <Input
            label="Deal Title"
            required
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
          />
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Value ($)"
              type="number"
              value={form.value}
              onChange={(e) => setForm({ ...form, value: e.target.value })}
            />
            <Select
              label="Priority"
              value={form.priority}
              onChange={(e) => setForm({ ...form, priority: e.target.value as DealPriority })}
              options={[
                { value: 'LOW', label: 'Low' },
                { value: 'MEDIUM', label: 'Medium' },
                { value: 'HIGH', label: 'High' },
                { value: 'URGENT', label: 'Urgent' },
              ]}
            />
          </div>
          <Select
            label="Deal Owner"
            value={form.ownerId}
            onChange={(e) => setForm({ ...form, ownerId: e.target.value })}
            options={[
              ...(teamMembers ?? []).map((m) => ({ value: m.id, label: m.name || m.email })),
              // Ensure current owner is always an option
              ...(!teamMembers?.find((m) => m.id === deal.ownerId)
                ? [{ value: deal.ownerId, label: deal.owner.name || 'Current Owner' }]
                : []),
            ]}
          />
          <Input
            label="Due Date"
            type="date"
            value={form.dueDate}
            onChange={(e) => setForm({ ...form, dueDate: e.target.value })}
          />
          <Textarea
            label="Description"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />
          <CustomFieldsForm
            entityType="DEAL"
            currentValues={customFields}
            onChange={setCustomFields}
          />
      </form>
    </Modal>
  );
}

function CloseDealFromDetail({
  dealId,
  open,
  onOpenChange,
}: {
  dealId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [closeReasonId, setCloseReasonId] = useState('');
  const [closeNotes, setCloseNotes] = useState('');
  const { data: reasons } = useCloseReasons();
  const closeDeal = useCloseDeal();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await closeDeal.mutateAsync({
      id: dealId,
      data: { closeReasonId, closeNotes: closeNotes || undefined },
    });
    onOpenChange(false);
  };

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="Close Deal"
      description="Select a reason for closing this deal."
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="submit" form="close-deal-form" variant="destructive" loading={closeDeal.isPending} disabled={!closeReasonId}>
            Close Deal
          </Button>
        </>
      }
    >
      <form id="close-deal-form" onSubmit={handleSubmit} className="space-y-4">
          <Select
            label="Close Reason"
            required
            value={closeReasonId}
            onChange={(e) => setCloseReasonId(e.target.value)}
            placeholder="Select a reason..."
            options={(reasons ?? []).map((r) => ({
              value: r.id,
              label: r.label,
            }))}
          />
          <Textarea
            label="Notes (optional)"
            value={closeNotes}
            onChange={(e) => setCloseNotes(e.target.value)}
            placeholder="Additional context..."
          />
      </form>
    </Modal>
  );
}

function ConvertDealDialog({
  deal,
  open,
  onOpenChange,
}: {
  deal: Deal;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [createOrg, setCreateOrg] = useState(true);
  const [orgName, setOrgName] = useState(deal.organization?.name || '');
  const [selectedContacts, setSelectedContacts] = useState<Record<string, boolean>>(
    () => {
      const initial: Record<string, boolean> = {};
      deal.dealContacts.forEach((dc) => {
        if (dc.contact.email) {
          initial[dc.contact.id] = true;
        }
      });
      return initial;
    }
  );
  const [createHubProject, setCreateHubProject] = useState(true);
  const [hubProjectName, setHubProjectName] = useState(deal.title);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState<{
    ecosystemOrgId?: string;
    ecosystemProjectId?: string;
    invitedContacts: number;
  } | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const inviteContacts = Object.entries(selectedContacts)
        .filter(([, selected]) => selected)
        .map(([contactId]) => contactId);

      const response = await conversionApi.convert(deal.id, {
        createOrg,
        orgName: createOrg ? orgName : undefined,
        inviteContacts,
        createHubProject,
        hubProjectName: createHubProject ? hubProjectName : undefined,
      });

      setResult(response);
    } catch {
      // Error is handled by the API interceptor
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    onOpenChange(false);
    if (result) {
      // Force refresh the deal data after successful conversion
      window.location.reload();
    }
  };

  if (result) {
    return (
      <Modal
        open={open}
        onOpenChange={handleClose}
        title="Conversion Successful"
        footer={<Button onClick={handleClose}>Done</Button>}
      >
          <div className="space-y-4">
            <div className="text-center py-4">
              <CheckCircle2 className="w-12 h-12 text-success mx-auto mb-3" />
              <p className="text-sm text-grey-600">
                Deal has been successfully converted to a client.
              </p>
            </div>
            <div className="space-y-2 text-sm text-grey-600">
              {result.ecosystemOrgId && (
                <p>Organization created successfully.</p>
              )}
              {result.ecosystemProjectId && (
                <p>Hub project created successfully.</p>
              )}
              {result.invitedContacts > 0 && (
                <p>{result.invitedContacts} contact{result.invitedContacts !== 1 ? 's' : ''} invited.</p>
              )}
            </div>
          </div>
      </Modal>
    );
  }

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="Convert to Client"
      description="Set up this won deal as a client in the ecosystem."
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="submit" form="convert-deal-form" loading={isSubmitting}>
            <ArrowRightCircle className="w-3.5 h-3.5" />
            Convert to Client
          </Button>
        </>
      }
    >
      <form id="convert-deal-form" onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={createOrg}
                onChange={(e) => setCreateOrg(e.target.checked)}
                className="rounded border-grey-300"
              />
              <span className="text-sm font-medium text-secondary">
                Create ecosystem organization
              </span>
            </label>
            {createOrg && (
              <Input
                label="Organization Name"
                required
                value={orgName}
                onChange={(e) => setOrgName(e.target.value)}
              />
            )}
          </div>

          {deal.dealContacts.length > 0 && (
            <div>
              <p className="text-sm font-medium text-secondary mb-2">Invite Contacts</p>
              <div className="space-y-2">
                {deal.dealContacts.map((dc) => (
                  <label key={dc.contact.id} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!selectedContacts[dc.contact.id]}
                      onChange={(e) =>
                        setSelectedContacts({
                          ...selectedContacts,
                          [dc.contact.id]: e.target.checked,
                        })
                      }
                      disabled={!dc.contact.email}
                      className="rounded border-grey-300"
                    />
                    <span className="text-sm text-grey-600">
                      {dc.contact.firstName} {dc.contact.lastName}
                      {dc.contact.email ? (
                        <span className="text-grey-400 ml-1">({dc.contact.email})</span>
                      ) : (
                        <span className="text-grey-400 ml-1">(no email)</span>
                      )}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}

          <div className="space-y-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={createHubProject}
                onChange={(e) => setCreateHubProject(e.target.checked)}
                className="rounded border-grey-300"
              />
              <span className="text-sm font-medium text-secondary">
                Create Hub project
              </span>
            </label>
            {createHubProject && (
              <Input
                label="Project Name"
                required
                value={hubProjectName}
                onChange={(e) => setHubProjectName(e.target.value)}
              />
            )}
          </div>

      </form>
    </Modal>
  );
}
